import { Box, useTheme } from '@mui/material';
import {
 BarElement,
 CategoryScale,
 Chart as ChartJS,
 Legend,
 LinearScale,
 Title,
 Tooltip,
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { WidgetItemType } from '../Dashboard.types';

ChartJS.register(
 CategoryScale,
 LinearScale,
 BarElement,
 Title,
 Tooltip,
 Legend,
);

interface DashboardWidgetBarChartGroupProps {
 widgetContent: WidgetItemType[];
}

const DashboardWidgetBarChartGroup = ({
 widgetContent,
}: DashboardWidgetBarChartGroupProps) => {
 const { t } = useTranslation();
 const theme = useTheme();

 const chartData = useMemo(() => {
  if (!widgetContent || widgetContent.length === 0) {
   return null;
  }

  const getAggregateValue = (item: WidgetItemType): number =>
   item.Aggregates['CargoNeto.sum'] ??
   item.Aggregates['neto.sum'] ??
   item.Aggregates['Neto.sum'] ??
   0;

  const labels = widgetContent.map((item) => item.Key);

  const likutisPradziaiData = widgetContent.map(() => 100000);

  const likutisPabaigaiData = widgetContent.map((item) =>
   getAggregateValue(item),
  );

  return {
   labels,
   datasets: [
    {
     label: t('dashboard.charts.balanceStart'),
     data: likutisPradziaiData,
     backgroundColor: theme.palette.primary.main,
     borderColor: theme.palette.primary.main,
     borderWidth: 1,
    },
    {
     label: t('dashboard.charts.balanceEnd'),
     data: likutisPabaigaiData,
     backgroundColor: theme.palette.success.main,
     borderColor: theme.palette.success.main,
     borderWidth: 1,
    },
   ],
  };
 }, [widgetContent, theme, t]);

 if (!chartData) {
  return null;
 }

 const options = useMemo(
  () => ({
   responsive: true,
   maintainAspectRatio: false,
   plugins: {
    legend: {
     position: 'top' as const,
     labels: {
      boxWidth: 12,
      padding: 8,
      font: {
       size: 12,
      },
     },
    },
    tooltip: {
     mode: 'index' as const,
     intersect: false,
    },
   },
   scales: {
    x: {
     display: true,
     title: {
      display: true,
      text: t('dashboard.charts.product'),
     },
    },
    y: {
     display: true,
     title: {
      display: true,
      text: t('dashboard.charts.value'),
     },
     beginAtZero: true,
    },
   },
  }),
  [t],
 );

 return (
  <Box
   sx={{
    height: '100%',
    width: '100%',
    position: 'relative',
   }}
  >
   <Bar data={chartData} options={options} />
  </Box>
 );
};

export default DashboardWidgetBarChartGroup;
